import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Flame, Trophy, X, Sparkles } from 'lucide-react';

const MESSAGES = {
  streak: [
    'Você está construindo um hábito que vai te tirar das dívidas. Cada dia organizado é um passo a menos para a liberdade financeira!',
    'Uma semana inteira no controle! É exatamente esse foco que separa quem sai do vermelho de quem continua preso. Orgulho de você!',
    'Consistência é tudo! Você provou que consegue manter o foco. Continue assim e logo as dívidas serão só uma lembrança.',
  ],
  budget: [
    'Você fechou a semana DENTRO da meta! É assim que se vence a dívida: um dia organizado de cada vez. Continue firme!',
    'Mandou bem demais! Gastar menos que o planejado é o segredo dos que conquistam a liberdade financeira. Você está mais perto!',
    'Meta batida sem estourar o orçamento! Seu eu do futuro agradece cada decisão consciente que você tomou esta semana.',
  ],
  debt: [
    'VOCÊ ZEROU SUA DÍVIDA! 🎉 Esse era o objetivo desde o primeiro dia, e você chegou lá. Nome limpo, mente leve e um futuro inteiro pela frente!',
    'Dívida QUITADA! Você fez o que a maioria não consegue: virou o jogo. Agora é hora de construir, poupar e investir no seu futuro.',
    'Liberdade financeira conquistada! Cada pagamento foi um passo, e hoje você cruzou a linha de chegada. Orgulho gigante de você!',
  ],
};

const CelebrationModal = ({ open, onClose, reason = 'streak', dias = 7 }) => {
  useEffect(() => {
    if (!open) return;
    const duration = 1800;
    const end = Date.now() + duration;
    const colors = ['#00FF94', '#33FFAD', '#FF9500', '#FFFFFF'];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 120, spread: 90, origin: { y: 0.4 }, colors });
  }, [open]);

  if (!open) return null;

  const msgList = MESSAGES[reason] || MESSAGES.streak;
  const msg = msgList[Math.floor(Math.random() * msgList.length)];
  const title = reason === 'budget'
    ? 'Semana dentro da meta!'
    : reason === 'debt'
      ? 'Dívida quitada! 🏆'
      : `${dias} dias seguidos! 🔥`;
  const Icon = reason === 'streak' ? Flame : Trophy;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="celebration-modal"
      >
        <motion.div
          className="relative w-full max-w-sm bg-gradient-to-br from-[#0F1A12] to-[#0A0A0A] border border-[#00FF94]/30 rounded-3xl p-8 text-center shadow-[0_0_60px_rgba(0,255,148,0.25)]"
          initial={{ scale: 0.7, y: 40 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
            data-testid="celebration-close-btn"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>

          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-5 rounded-3xl bg-[#00FF94]/15 border border-[#00FF94]/30 flex items-center justify-center"
          >
            <Icon className={`w-10 h-10 ${reason === 'budget' ? 'text-[#FF9500] fill-[#FF9500]' : 'text-[#FF9500] fill-[#FF9500]'}`} />
          </motion.div>

          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00FF94]/10 text-[#00FF94] text-xs font-semibold uppercase tracking-wider mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Pequena grande vitória
          </span>

          <h2 className="text-2xl font-black text-white mb-3" data-testid="celebration-title">{title}</h2>
          <p className="text-gray-300 leading-relaxed mb-6 text-sm">{msg}</p>

          <button
            onClick={onClose}
            className="w-full bg-[#00FF94] text-black font-bold py-3.5 rounded-full hover:bg-[#33FFAD] transition-all shadow-[0_0_25px_rgba(0,255,148,0.3)]"
            data-testid="celebration-continue-btn"
          >
            Continuar organizando
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CelebrationModal;
